/**
 * Work Queues data contracts.
 * UI shapes plus the SOR record payloads they are mapped from (see work-queues-api-mappers.ts).
 */

export type WorkQueueRoutingMethod =
  | 'manual'
  | 'round_robin'
  | 'load_balanced'
  | 'rules_based'
  | 'territory'
  | 'shared_pull'
  | 'ai_recommended';

export type WorkQueueRuleOperator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'greater_than';

export type WorkQueueClaimOrder =
  | 'oldest_first'
  | 'due_soonest'
  | 'highest_priority';

export interface WorkQueueEntityType {
  /** SOR definition id; saved as assignmentPolicies[].sourceDefinitionId and stored in WorkQueue.entity_type */
  uuid: string;
  /** Definition key, e.g. `slm.insp.inspection` */
  key: string;
  /** core.workqueue.queue_entities record holding the `enabled` switch */
  record_id: string;
  record_revision: number;
  name: string;
  description: string;
  icon: string;
  source: string;
  is_enabled: boolean;
  routing_fields: string[];
  statuses: string[];
}

export interface WorkQueueMember {
  user_id: string;
  name: string;
  email: string;
}

export interface WorkQueueTeamRoleMember extends WorkQueueMember {
  /** Not provided by the directory yet */
  open_items?: number;
}

export interface WorkQueueTeamRole {
  /** core.directory.role record id; users reference it in data.roleIds */
  id: string;
  /** Role key (SOR `roleKey`, e.g. `data_operator`), saved on the queue's assignment policy */
  name: string;
  /** Display name; falls back to `name` */
  label?: string;
}

export interface WorkQueueRoutingRule {
  rule_id: string;
  field: string;
  operator: WorkQueueRuleOperator;
  value: string;
  /** user_id of the queue member that receives matching items */
  assign_to: string;
}

/** Settings for every routing method; only the fields for the selected method are used. */
export interface WorkQueueRoutingOptions {
  // manual + ai_recommended
  assigner_role: string;
  // manual
  unassigned_alert_hours: number | null;
  allow_self_assign: boolean;
  notify_assignee: boolean;
  // round_robin + load_balanced
  skip_out_of_office: boolean;
  // load_balanced
  max_open_per_member: number | null;
  // shared_pull
  claim_order: WorkQueueClaimOrder;
  release_after_idle_minutes: number | null;
  // ai_recommended
  min_confidence_percent: number | null;
  // rules_based
  rules: WorkQueueRoutingRule[];
  // territory: user_id -> comma separated states / ZIP prefixes
  territories: Record<string, string>;
}

export interface WorkQueue {
  queue_id: string;
  name: string;
  description: string;
  /** WorkQueueEntityType.uuid (SOR sourceDefinitionId) */
  entity_type: string;
  team_role: string;
  members: WorkQueueMember[];
  routing_method: WorkQueueRoutingMethod;
  routing_options: WorkQueueRoutingOptions;
  sla_hours: number;
  /** NOT_STARTED escalationPolicies[] entry's trigger.afterMinutes, in hours */
  escalation_hours: number | null;
  /** core.directory.role record id (the escalation entry's target.roleId) */
  escalation_role: string;
  send_reminders: boolean;
  is_active: boolean;
  /** Enabled queue_member records; undefined when they couldn't be loaded */
  member_count?: number;
  /** SOR record this queue was loaded from; used to preserve unmapped fields on update */
  source_record?: WorkQueueSourceRecord;
}

export interface WorkQueueRoutingMethodOption {
  key: WorkQueueRoutingMethod;
  label: string;
  description: string;
  bestFor: string;
  icon: string;
  tag?: string;
}

export const WORK_QUEUE_ROUTING_METHODS: WorkQueueRoutingMethodOption[] = [
  {
    key: 'manual',
    label: 'Manual',
    description:
      'A dispatcher reviews each new item and assigns it to a team member.',
    bestFor: 'Best for small teams and judgment-heavy work',
    icon: 'mdi:hand-back-right-outline',
  },
];

export const WORK_QUEUE_ROUTING_METHOD_LABELS = Object.fromEntries(
  WORK_QUEUE_ROUTING_METHODS.map((method) => [method.key, method.label])
) as Record<WorkQueueRoutingMethod, string>;

export const WORK_QUEUE_RULE_OPERATOR_LABELS: Record<
  WorkQueueRuleOperator,
  string
> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  greater_than: 'greater than',
};

export const WORK_QUEUE_CLAIM_ORDER_LABELS: Record<
  WorkQueueClaimOrder,
  string
> = {
  oldest_first: 'Oldest first',
  due_soonest: 'Due soonest',
  highest_priority: 'Highest priority',
};

/** SOR definition that stores Work Queue records (`data` = WorkQueueRecordData) */
export const WORK_QUEUE_RECORD_DEFINITION = 'core.workqueue.queue';

export interface WorkQueueAssignmentTarget {
  /** 'ROLE' | 'USER' | 'USER_FILTER' */
  type: string;
  userId: string | null;
  roleKey: string | null;
  userFilter: unknown;
}

export interface WorkQueueAssignmentPolicy {
  enabled?: boolean;
  purpose: string;
  condition?: unknown;
  policyKey: string;
  precedence?: number;
  /** INHERIT falls back to the queue-level assignmentMethod */
  assignmentMethod?: string;
  assignmentTarget: WorkQueueAssignmentTarget;
  sourceDefinitionId: string;
  // Remaining schema fields are passed through untouched on update
  [key: string]: unknown;
}

export type WorkQueueAssignmentMethod =
  | 'MANUAL'
  | 'PULL'
  | 'ROUND_ROBIN'
  | 'LEAST_LOAD'
  | 'SKILL_BASED';

/** `data` payload of a `core.workqueue.queue` SOR record */
export interface WorkQueueRecordData {
  name?: string;
  description?: string;
  queueKey?: string;
  capacityLimit?: number | null;
  defaultPriority?: number;
  fallbackQueueId?: string | null;
  /** 'ACTIVE' | 'PAUSED' | 'DRAINING' | 'CLOSED' */
  operatingStatus?: string;
  /** A WorkQueueAssignmentMethod; typed as string because stored values aren't validated */
  assignmentMethod?: string;
  assignmentPolicies?: WorkQueueAssignmentPolicy[];
  responseSlaMinutes?: number | null;
  completionSlaMinutes?: number | null;
  preventSelfApproval?: boolean;
  /** Per-queue switch per entity type; a type that isn't listed is enabled */
  entityTypes?: WorkQueueEntityTypeSwitch[];
  /** 'IN_APP' | 'EMAIL'; empty means don't notify */
  assigneeNotificationMethods?: string[];
  escalationPolicies?: WorkQueueEscalationPolicy[];
}

export interface WorkQueueEntityTypeSwitch {
  sourceDefinitionId: string;
  enabled?: boolean;
}

export interface WorkQueueEscalationPolicy {
  escalationKey: string;
  level: number;
  enabled?: boolean;
  trigger: {
    /** 'NOT_STARTED' | 'NOT_COMPLETED' | 'WAITING' */
    when: string;
    afterMinutes: number;
  };
  target: {
    /** 'ROLE' | 'USER' | 'QUEUE_SUPERVISORS' | 'ASSIGNEE_MANAGER' | 'ASSIGNEE' */
    type: string;
    /** core.directory.role record id; ROLE targets only */
    roleId?: string;
    userId?: string;
  };
  /** 'NOTIFY' | 'REASSIGN' | 'TRANSFER' | 'RAISE_PRIORITY' */
  action: string;
  repeatEveryMinutes?: number | null;
  // Remaining schema fields are passed through untouched on update
  [key: string]: unknown;
}

/** SOR definition that stores queue actions (`data` = WorkQueueActionRecordData) */
export const WORK_QUEUE_ACTION_DEFINITION = 'core.workqueue.queue_action';

/** `data` payload of a queue action SOR record */
export interface WorkQueueActionRecordData {
  queueId: string;
  actionKey: string;
  label: string;
  kind:
    | 'QUEUE_OPERATION'
    | 'ENTITY_TRANSITION'
    | 'ENTITY_UPDATE'
    | 'MANUAL_ACKNOWLEDGEMENT';
  queueOperation?: string | null;
  requiredQueueRole?: 'AGENT' | 'SUPERVISOR';
  allowedItemStates?: string[];
  sourceDefinitionId?: string | null;
  requireComment?: boolean;
  enabled?: boolean;
}

export interface WorkQueueSourceRecord {
  id: string;
  revision: number;
  data: WorkQueueRecordData;
}

/** SOR definition that stores queue members (`data` = WorkQueueMemberRecordData) */
export const WORK_QUEUE_MEMBER_DEFINITION = 'core.workqueue.queue_member';

/** `data` payload of a queue member SOR record */
export interface WorkQueueMemberRecordData {
  queueId: string;
  memberKey: string;
  subjectType: 'USER' | 'ROLE';
  /** core.directory.user record id (uuid); set for USER members */
  userId: string | null;
  /** core.directory.role data.key; ROLE members only (the schema requires null for USER) */
  roleKey: string | null;
  /** core.directory.role record id; ROLE members only (the schema requires null for USER) */
  roleId?: string | null;
  queueRole?: 'AGENT' | 'SUPERVISOR' | 'OBSERVER';
  enabled?: boolean;
}

/** Subset of the SOR `DefinitionOut` */
export interface SorDefinitionSummary {
  id: string;
  key: string;
  qualified_key: string;
  name: string;
  description: string | null;
  /** 'MASTER' | 'CONFIGURATION' | 'TRANSACTIONAL' */
  classification: string;
  is_active: boolean;
}

/** SOR definition listing the queue-enabled entity types (`data` = WorkQueueEntityRecordData) */
export const WORK_QUEUE_ENTITY_DEFINITION = 'core.workqueue.queue_entities';

/** `data` payload of a `core.workqueue.queue_entities` record */
export interface WorkQueueEntityRecordData {
  /** Off stops new routing for the entity and cancels its unclaimed work */
  enabled?: boolean;
  description?: string;
  /** The registered definition's id (assignmentPolicies[].sourceDefinitionId) */
  definitionId: string;
}

/** `expanded.definition` of a queue_entities record queried with expand: ['definition'] */
export interface WorkQueueEntityExpandedDefinition {
  id: string;
  display?: string | null;
  key?: string | null;
}

/** Directory definitions holding team roles and the users assigned to them */
export const DIRECTORY_ROLE_DEFINITION = 'core.directory.role';
export const DIRECTORY_USER_DEFINITION = 'core.directory.user';

/** `data` payload of a `core.directory.role` record */
export interface DirectoryRoleData {
  key: string;
  name?: string;
  active?: boolean;
  description?: string;
}

/** `data` payload of a `core.directory.user` record */
export interface DirectoryUserData {
  roleIds?: string[];
  /** Identity subject, e.g. "samlp|circuitry-dev|jane@company.com" */
  subjectId?: string;
  displayName?: string;
}
